<script setup lang="ts">
import { Dialog } from '@ark-ui/vue'

interface Props {
  isOpen: boolean
  canClose?: boolean
  hasCloseButton?: boolean
  headerClasses?: string
  teleportTo?: string
  showBackdrop?: boolean
  dialogClasses?: string
}

interface Emits {
  (e: 'close'): void
}

const emits = defineEmits<Emits>()
const props = withDefaults(defineProps<Props>(), {
  canClose: true,
  hasCloseButton: false,
  showBackdrop: true,
})

const close = () => {
  emits('close')
}

watch(() => props.isOpen, (isOpen) => {
  window.parent.postMessage(
    { name: isOpen ? 'dialog-open' : 'dialog-close', payload: {} },
    import.meta.env.VITE_SH_URL,
  )
})

window.addEventListener('message', (event) => {
  if (event.data?.name === 'dialog-close') {
    close();
  }
})


const handleInteractOutside = () => {
  if (!props.canClose) return
  close()
}
const appStore = useAppStore()
const slots = useSlots()
const hasTitle = computed(() => !!slots.title)
</script>

<script lang="ts">
export default {
  inheritAttrs: false,
}
</script>

<template>
  <Dialog.Root
    :open="props.isOpen"
    :close-on-interact-outside="props.canClose"
    :prevent-scroll="true"
    lazy-mount
    unmount-on-exit
    @interact-outside="handleInteractOutside"
  >
    <template v-if="props.teleportTo">
      <Teleport defer :to="props.teleportTo">
        <!-- Backdrop (optional for teleported dialogs) -->
        <Dialog.Backdrop
          v-if="props.showBackdrop"
          class="absolute inset-0 bg-brand-dark bg-opacity-50 transition-opacity z-20"
        />

        <!-- Content -->
        <Dialog.Positioner class="absolute inset-0 z-30 overflow-y-auto">
          <div :class="['flex min-h-full items-end justify-center text-center', dialogClasses]">
            <Dialog.Content
              class="w-full transform overflow-hidden rounded-t-xl bg-white text-left shadow-xl transition-all data-[state=closed]:animate-slideOut data-[state=open]:animate-slideIn"
              :class="{'rounded-b-xl': dialogClasses}"
            >
              <div class="relative flex w-full items-center bg-white">
                <!-- Close Button -->
                <div
                  v-if="props.hasCloseButton"
                  class="absolute right-3.5 top-3"
                >
                  <Dialog.CloseTrigger
                    class="rounded-md bg-transparent text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0"
                    @click="close"
                  >
                    <span class="sr-only">Close</span>
                    <HeroiconsXMark class="h-5 w-5" aria-hidden="true" />
                  </Dialog.CloseTrigger>
                </div>
                <div class="w-full sm:mt-0 sm:text-left">
                  <div class="px-4 py-3 " :class="headerClasses, {'border-b border-gray-200' : appStore.spotHopperDesktopDetailsPage && hasTitle }">
                    <Dialog.Title class="font-medium leading-6">
                      <slot name="title" />
                    </Dialog.Title>
                  </div>

                  <div id="app-dialog-content" class="text-gray-500" :class="{'p-2' : appStore.spotHopperDesktopDetailsPage }">
                    <slot name="content" />
                  </div>
                </div>
              </div>
            </Dialog.Content>
          </div>
        </Dialog.Positioner>
      </Teleport>
    </template>

    <template v-else>
      <Teleport to="body">
        <!-- Backdrop -->
        <Dialog.Backdrop
          class="fixed inset-0 bg-brand-dark bg-opacity-50 transition-opacity z-10"
        />

        <!-- Content -->
        <Dialog.Positioner class="fixed inset-0 z-10 overflow-y-auto">
          <div class="flex min-h-full items-end justify-center text-center">
            <Dialog.Content
              :class="['w-full transform overflow-hidden rounded-t-xl bg-white text-left shadow-xl transition-all data-[state=closed]:animate-slideOut data-[state=open]:animate-slideIn', dialogClasses]"
            >
              <div class="relative flex w-full items-center bg-white">
                <!-- Close Button -->
                <div
                  v-if="props.hasCloseButton"
                  class="absolute right-3.5 top-3"
                >
                  <Dialog.CloseTrigger
                    class="rounded-md bg-transparent text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0"
                    @click="close"
                  >
                    <span class="sr-only">Close</span>
                    <HeroiconsXMark class="h-5 w-5" aria-hidden="true" />
                  </Dialog.CloseTrigger>
                </div>
                <div class="w-full sm:mt-0 sm:text-left">
                  <div class="px-4 py-3" :class="headerClasses">
                    <Dialog.Title class="font-medium leading-6 text-gray-900">
                      <slot name="title" />
                    </Dialog.Title>
                  </div>

                  <div id="app-dialog-content" class="text-gray-500">
                    <slot name="content" />
                  </div>
                </div>
              </div>
            </Dialog.Content>
          </div>
        </Dialog.Positioner>
      </Teleport>
    </template>
  </Dialog.Root>
</template>
